<template>
  <div v-if="firm" class="ui-panel">
    <div class="ui-panel__head">
      <ui-avatar
        :text="firmInitials(firm)"
        :tone="firm.isChild ? 'blue' : 'accent'"
        big
      />
      <div>
        <small>{{ firm.email || "—" }}</small>
      </div>
    </div>

    <div class="ui-tags">
      <ui-chip :tone="stateTone(firm)" dot>{{ stateText(firm) }}</ui-chip>
      <ui-chip v-if="link" :tone="link.internal ? 'blue' : 'plain'">{{
        $t(
          link.internal ? "delivery_firms.coop.internal" : "delivery_firms.coop.external"
        )
      }}</ui-chip>
      <ui-chip v-if="firm.isChild" tone="blue">{{ $t("delivery_firms.branch") }}</ui-chip>
    </div>

    <ui-banner v-if="firm.active !== 1" tone="warn">{{
      $t(
        firm.active === 2
          ? "delivery_firms.panel.deleted_note"
          : "delivery_firms.panel.inactive_note"
      )
    }}</ui-banner>
    <ui-banner
      v-else-if="state === 'paused'"
      tone="warn"
      icon="mdi-pause-circle-outline"
      >{{ $t("delivery_firms.panel.paused_note") }}</ui-banner
    >
    <ui-banner v-if="billingMissing" tone="warn" icon="mdi-cash-remove">
      {{ $t("delivery_firms.panel.unpaid_note") }}
      <button
        v-if="caps.cooperation"
        type="button"
        class="ui-link"
        @click="shell.openCooperation(firm.id)"
      >
        {{ $t("delivery_firms.panel.unpaid_fix") }}
      </button>
    </ui-banner>

    <div v-if="caps.toggle" class="ui-switch-line">
      <span>
        <b>{{ $t("delivery_firms.switch.label") }}</b>
        <small>{{ $t("delivery_firms.switch.hint") }}</small>
      </span>
      <ui-switch
        :value="Boolean(link.activeCompany)"
        :disabled="firm.active !== 1"
        :label="`${$t('delivery_firms.switch.label')}: ${firmName(firm)}`"
        @input="(on) => shell.toggleOrders(firm, on)"
      />
    </div>

    <div class="ui-actions">
      <ui-button
        v-if="caps.edit"
        variant="primary"
        icon="mdi-pencil-outline"
        @click="shell.openForm(firm.id)"
        >{{ $t("delivery_firms.panel.edit") }}</ui-button
      >
      <ui-button
        v-if="caps.cooperation"
        icon="mdi-handshake-outline"
        @click="shell.openCooperationFor(firm)"
        >{{
          $t(
            isAdmin
              ? "delivery_firms.panel.restaurants_open"
              : "delivery_firms.panel.cooperation"
          )
        }}</ui-button
      >
      <ui-button
        v-if="caps.users"
        icon="mdi-account-group-outline"
        :to="shell.usersRoute(firm)"
        >{{ $t("users") }} {{ firm.usersCount }}</ui-button
      >
      <ui-button
        v-if="caps.edit && !firm.isChild && shell.canCreate"
        icon="mdi-source-branch-plus"
        @click="shell.openForm(0, firm.id)"
        >{{ $t("delivery_firms.panel.add_branch") }}</ui-button
      >
    </div>

    <ui-section :title="$t('delivery_firms.sections.contact')">
      <ui-kv :rows="contactRows">
        <template #value="{ row }">
          <button
            v-if="row.key === 'parent'"
            type="button"
            class="ui-link"
            @click="shell.openFirm(parent.id)"
          >
            {{ firmName(parent) }}
          </button>
          <template v-else>{{
            row.value || $t("delivery_firms.panel.not_entered")
          }}</template>
        </template>
      </ui-kv>
    </ui-section>

    <ui-section :title="$t('delivery_firms.sections.legal')">
      <ui-kv :rows="legalRows" :empty-text="$t('delivery_firms.panel.not_entered')" />
    </ui-section>

    <ui-section v-if="link" :title="$t('delivery_firms.sections.cooperation')">
      <ui-kv :rows="linkRows" :empty-text="$t('delivery_firms.panel.not_entered')" />
    </ui-section>

    <ui-section v-if="isAdmin" :title="$t('delivery_firms.sections.platform')">
      <ui-kv :rows="platformRows" :empty-text="$t('delivery_firms.panel.not_entered')" />
    </ui-section>

    <ui-section
      v-if="isAdmin && firm.links.length"
      :title="`${$t('delivery_firms.sections.restaurants')} · ${firm.links.length}`"
    >
      <ul class="ui-rows">
        <li v-for="item in firm.links" :key="item.id">
          <span>{{ toLatin(item.restaurantName) || `#${item.restaurantId}` }}</span>
          <small>
            {{
              $t(
                item.internal
                  ? "delivery_firms.coop.internal"
                  : "delivery_firms.coop.external"
              )
            }}<template v-if="item.payType">
              · {{ payAmountText(item.payType, item.paying, firmCurrency(firm)) }}
              {{ payTypeLabel(item.payType).toLowerCase() }}</template
            >
          </small>
        </li>
      </ul>
    </ui-section>

    <ui-section
      v-if="firm.children.length"
      :title="`${$t('delivery_firms.sections.branches')} · ${firm.children.length}`"
    >
      <ul class="ui-rows">
        <li v-for="child in firm.children" :key="child.id">
          <button type="button" class="ui-link" @click="shell.openFirm(child.id)">
            {{ firmName(child) }}
          </button>
          <ui-chip :tone="stateTone(child)" dot>{{ stateText(child) }}</ui-chip>
        </li>
      </ul>
    </ui-section>
  </div>
</template>

<script>
import hasFirmLabels from "../hasFirmLabels";

/** Kartica firme u desnom panelu: stanje, radnje, kontakt, pravni podaci, saradnja sa izabranim restoranom, a admin vidi i naplatu platforme i sve restorane. */
export default {
  name: "FirmPanel",
  mixins: [hasFirmLabels],
  inject: ["reportShell"],
  props: { firmId: { type: Number, required: true } },
  computed: {
    shell() {
      return this.reportShell;
    },
    firm() {
      return this.shell.flat.find((item) => item.id === this.firmId) || null;
    },
    parent() {
      return this.firm && this.firm.isChild
        ? this.shell.firms.find((item) => item.id === this.firm.parentId) || null
        : null;
    },
    state() {
      return this.stateOf(this.firm);
    },
    link() {
      return this.linkOf(this.firm);
    },
    caps() {
      return this.can(this.firm);
    },
    billingMissing() {
      return this.isBillingMissing(this.firm);
    },
    isAdmin() {
      return this.userType === "admin";
    },
    contactRows() {
      const f = this.firm;
      const rows = [
        { label: this.$t("email"), value: f.email },
        { label: this.$t("phone"), value: f.phone },
        { label: this.$t("city"), value: this.cityLabel(f) },
        { label: this.$t("contact_person"), value: this.toLatin(f.contact) },
        { label: this.$t("owner"), value: this.toLatin(f.owner) },
      ];
      // matična firma je veza, ne tekst: red je `parent` sa vlastitim prikazom
      if (this.parent)
        rows.push({ key: "parent", label: this.$t("parent_company"), empty: false });
      return rows;
    },
    legalRows() {
      const f = this.firm;
      return [
        { label: this.$t("jib"), value: f.jib },
        { label: this.$t("pib"), value: f.pib },
        { label: this.$t("contract"), value: f.contract },
        { label: this.$t("select_currency"), value: f.currency },
        { label: this.$t("note"), value: this.toLatin(f.note) },
      ];
    },
    linkRows() {
      const l = this.link;
      const yes = (on) => this.$t(on ? "yes" : "no");
      return [
        { label: this.$t("status_restoran"), value: yes(l.activeRestaurant) },
        { label: this.$t("status_company"), value: yes(l.activeCompany) },
        { label: this.$t("pay_type"), value: this.payTypeLabel(l.payType) },
        {
          label: this.$t("amount"),
          value: this.payAmountText(l.payType, l.paying, this.firmCurrency(this.firm)),
        },
        { label: this.$t("internal"), value: yes(l.internal) },
      ];
    },
    platformRows() {
      const f = this.firm;
      return [
        { label: this.$t("platform_pay_type"), value: this.payTypeLabel(f.platformType) },
        {
          label: this.$t("amount"),
          value: this.payAmountText(
            f.platformType,
            f.platformAmount,
            this.firmCurrency(f)
          ),
        },
        {
          label: this.$t("show_price_breakdown"),
          value: this.$t(f.showBreakdown ? "yes" : "no"),
        },
      ];
    },
  },
};
</script>
